// Upload de un producto nuevo desde Interfaz V2.
//
// Replica el flujo de `SeleccionarProductosAnuncio.handleSaveProduct`:
//   1. INSERT en `productos` (con nombre placeholder si vacío)
//   2. INSERT en `variantes` (linked a product_id)
//   3. Para cada imagen:
//      0. Normalizar formato (HEIC/HEIF/AVIF → JPEG) y reducir a máx 2000px
//      a. Upload full a `productos/<company>/<variant>/full/<filename>`
//      b. Generar thumbnail opt (300px, JPEG 80%) y upload a `.../opt/...`
//      c. INSERT en `productos_imagenes` con la filename + flags
//   4. Webhook `categorizar_producto` (modo crear) para que el backend
//      le ponga nombre con IA si el user no lo definió.
//
// Devuelve el `variantId` creado para que el caller pueda auto-seleccionar
// la nueva variante en su UI.

import { supabase } from '../../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../../config';
import { normalizeImageForUpload } from '../../utils/heicConvert';
import { resizeImageIfNeeded } from '../../utils/imageResize';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const THUMBNAIL_TARGET = 300; // max side en px
const THUMBNAIL_QUALITY = 0.8;
const CATEGORIZAR_WEBHOOK_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/categorizar_producto';

export interface UploadNewProductArgs {
  companyId: string;
  userId: string;
  /** Nombre del producto. Si vacío, el backend lo asigna con IA. */
  name: string;
  /** Imágenes a subir. Mínimo 1. */
  files: File[];
  /** Marca activa del user. Si está provista, queda asociada al
   *  producto. Sin esto, "Seleccionar productos" (que hace inner join
   *  por brand) no muestra el nuevo producto cuando el user tiene una
   *  marca activa. Replica el comportamiento de
   *  SeleccionarProductosAnuncio.handleSaveProduct. */
  brandId?: string | null;
}

export interface UploadNewProductResult {
  variantId: string;
  productId: string;
  /** El nombre que quedó guardado (placeholder si el user no puso uno). */
  finalName: string;
}

/**
 * Genera un thumbnail JPEG escalado a max THUMBNAIL_TARGET px del lado
 * más largo. Vuelve un Blob listo para upload.
 */
async function generateThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('No se pudo obtener el contexto del canvas'));
      return;
    }
    img.onload = () => {
      let { width, height } = img;
      if (width > height) {
        if (width > THUMBNAIL_TARGET) {
          height = Math.round(height * (THUMBNAIL_TARGET / width));
          width = THUMBNAIL_TARGET;
        }
      } else if (height > THUMBNAIL_TARGET) {
        width = Math.round(width * (THUMBNAIL_TARGET / height));
        height = THUMBNAIL_TARGET;
      }
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Falló blob'))),
        'image/jpeg',
        THUMBNAIL_QUALITY,
      );
    };
    img.onerror = () => reject(new Error('Falló cargar imagen'));
    img.src = URL.createObjectURL(file);
  });
}

export async function uploadNewProduct({
  companyId,
  userId,
  name,
  files,
  brandId = null,
}: UploadNewProductArgs): Promise<UploadNewProductResult> {
  if (files.length === 0) throw new Error('Se necesita al menos una imagen');
  for (const f of files) {
    if (f.size > MAX_FILE_SIZE) {
      throw new Error('Una de las imágenes supera el límite de 100 MB.');
    }
  }

  // Resolver el nombre placeholder si el user no escribió ninguno —
  // contamos cuántos productos tiene la empresa y armamos "Producto N+1".
  // El backend (webhook) lo reemplaza después por el nombre IA.
  let effectiveName = name.trim();
  const userProvidedName = !!effectiveName;
  if (!effectiveName) {
    const { count } = await supabase
      .from('productos')
      .select('id', { count: 'exact', head: true })
      .eq('id_company', companyId);
    effectiveName = `Producto ${(count ?? 0) + 1}`;
  }

  // 1. Crear producto — incluir `brand_id` si hay marca activa, así
  // queda visible en "Seleccionar productos" (que filtra por brand
  // via inner join cuando hay una marca seleccionada).
  const productoPayload: Record<string, any> = {
    id_company: companyId,
    nombre: effectiveName,
    tiene_variantes: false,
  };
  if (brandId) productoPayload.brand_id = brandId;
  const { data: producto, error: productoError } = await supabase
    .from('productos')
    .insert(productoPayload)
    .select('id')
    .single();
  if (productoError) throw productoError;
  const productId = String((producto as any).id);

  // 2. Crear variante
  const { data: variant, error: variantError } = await supabase
    .from('variantes')
    .insert({
      id_company: companyId,
      nombre: effectiveName,
      activo: true,
      product_id: productId,
    })
    .select('id')
    .single();
  if (variantError) throw variantError;
  const variantId = String((variant as any).id);

  // 3. Upload images + insert filas en productos_imagenes
  for (let i = 0; i < files.length; i++) {
    // Normalizar formato y tamaño igual que el upload de "Seleccionar
    // productos". Sin esto, las fotos de iPhone (.heic/.avif) se subían
    // crudas: el thumbnail fallaba y el `full` quedaba en un formato que el
    // visor/backend no renderiza. También bajamos a máx 2000px para no
    // subir el original completo.
    let file: File;
    try {
      file = await normalizeImageForUpload(files[i]);
    } catch (convErr) {
      console.error('[productUploadApi] conversión de formato falló', convErr);
      throw new Error(
        'No pudimos convertir la imagen (HEIC/AVIF). Probá con una imagen en formato png o jpg.',
      );
    }
    try {
      file = await resizeImageIfNeeded(file, 2000, 0.85);
    } catch (resizeErr) {
      // El resize es una optimización: si falla, subimos la imagen ya
      // normalizada sin redimensionar en vez de abortar.
      console.warn(
        '[productUploadApi] resize falló, se sube sin redimensionar',
        resizeErr,
      );
    }
    const timestamp = Date.now() + i; // pequeño jitter para evitar colisión
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const fileBaseName = `${timestamp}_${sanitizedName}`;

    // 3a. Upload full
    const fullPath = `${companyId}/${variantId}/full/${fileBaseName}`;
    const { error: fullErr } = await supabase.storage
      .from('productos')
      .upload(fullPath, file);
    if (fullErr) throw fullErr;

    // 3b. Generar + upload opt (si falla, seguimos con solo full)
    try {
      const thumb = await generateThumbnail(file);
      const optPath = `${companyId}/${variantId}/opt/${fileBaseName}`;
      const { error: optErr } = await supabase.storage
        .from('productos')
        .upload(optPath, thumb);
      if (optErr) {
        console.warn(
          '[productUploadApi] opt upload falló, seguimos con full',
          optErr,
        );
      }
    } catch (thumbErr) {
      console.warn(
        '[productUploadApi] thumbnail falló, seguimos con full',
        thumbErr,
      );
    }

    // 3c. Insert DB row
    const { error: imageError } = await supabase
      .from('productos_imagenes')
      .insert({
        id_company: companyId,
        variante_id: variantId,
        url: fileBaseName,
        principal: i === 0,
        orden: i + 1,
      });
    if (imageError) throw imageError;
  }

  // 4. Webhook de categorización IA — fire-and-forget. Si el user no dio
  // nombre, el backend lo completa con IA. Sino, solo categoriza.
  try {
    fetch(CATEGORIZAR_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify({
        user_id: userId,
        product_id: productId,
        modo: 'crear',
        completar_nombre: !userProvidedName,
      }),
    }).catch(() => {
      /* silent — categorización IA no es crítica */
    });
  } catch {
    /* noop */
  }

  return { variantId, productId, finalName: effectiveName };
}
